import * as Sentry from '@sentry/vue'
import { Capacitor } from '@capacitor/core'
import { isLikelyNetworkEvent } from '@/lib/API/network-error'

const dsn = 'https://d5087e7d23fb4178bce6d062d36dc5aa@o923658.ingest.sentry.io/4504366037204992'
const viteEnv = import.meta.env.VITE_APP_ENVIRONMENT || 'local'
const sendSentryLogs = ['stage', 'staging', 'production'].includes(viteEnv)

const isChunkLoadError = (message) =>
    /Loading chunk [\d]+ failed/.test(message) || /Failed to fetch dynamically imported module/.test(message)

const isShareCanceledError = (value) => {
    const message = String(value?.message || value?.value || value || '').toLowerCase()
    return message.includes('share canceled') || message.includes('share cancelled')
}

const isShareCanceledEvent = (event, hint) =>
    isShareCanceledError(hint?.originalException) ||
    isShareCanceledError(event?.message) ||
    event?.exception?.values?.some((exception) => isShareCanceledError(exception))

const initSentry = (app, router, isBuildForMobile) => {
    let integrations = []

    if (sendSentryLogs) {
        integrations = [
            Sentry.browserTracingIntegration({ router }),
            Sentry.captureConsoleIntegration({
                levels: ['warn', 'error'],
            }),
            // One session per navigation, as before v11 (which starts one per app launch).
            Sentry.browserSessionIntegration({ lifecycle: 'route' }),
        ]
    } else {
        console.info(`[Sentry] Disabled for environment: ${viteEnv}`)
        return
    }

    const sentryDist = import.meta.env.VITE_APP_PLATFORM || 'web'
    const scope = { isBuildForMobile: isBuildForMobile, platform: Capacitor.getPlatform() }

    Sentry.init({
        app,
        dsn,
        integrations,
        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
        beforeSend(event, hint) {
            const originalException = hint?.originalException
            // Chunk load errors - ignore silently, let the app handle it gracefully
            if (isChunkLoadError(event.message) || isChunkLoadError(originalException)) {
                return null
            }

            if (isLikelyNetworkEvent(event, hint)) {
                return null
            }

            if (isShareCanceledEvent(event, hint)) {
                return null
            }

            if (!sendSentryLogs) {
                console.error(
                    ['local', 'development'].includes(viteEnv) ? (originalException ?? event.message) : event
                )
                return null
            }
            return event
        },
        // v11 defaults kept at their v10 values: no stack trace on captured
        // messages (it would regroup every issue), and no IP address of users.
        attachStacktrace: false,
        dataCollection: { userInfo: false },
        tracesSampleRate: 0.3,
        environment: viteEnv,
        trackComponents: true,
        // Errors read the tags; spans, since v11, the attributes only.
        initialScope: { tags: scope, attributes: scope },
        release: APP_NAME + '@' + APP_VERSION,
        dist: sentryDist,
    })
}

export { initSentry }
